import { useState, useEffect } from "react";
import { PersonalAccountBeneficiary } from "@/types";
import PersonalAccountBeneficiaryApiClient from "@/api/personalAccountBeneficiaryApiClient";
import { useAuthenticatedApiClient } from "./useAuthenticatedApiClient";
import { useFinancialApplicationCoreData } from "./useFinancialApplicationCoreData";
import { AppToasterV2 } from "@ucl/library";
import { useFinancialProducts } from "./useFinancialProducts";
import FinancialApplicationApiClient from "@/api/financialApplicationApiClient";

export const usePersonalAccountBequests = (
  financialApplicationId: string,
  personalAccountBeneficiaryId: string
) => {
  const [beneficiary, setBeneficiary] = useState<PersonalAccountBeneficiary | null>(null);
  const [selectedAccountId, setSelectedAccountId] = useState<string>("");
  const [bequestPercentage, setBequestPercentage] = useState<number | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isAddingBequest, setIsAddingBequest] = useState<boolean>(false);
  const [updatingBequestIds, setUpdatingBequestIds] = useState<Set<string>>(new Set());
  const [removingBequestIds, setRemovingBequestIds] = useState<Set<string>>(new Set());

  const personalAccountBeneficiaryApiClient = useAuthenticatedApiClient(PersonalAccountBeneficiaryApiClient);
  const financialApplicationApiClient = useAuthenticatedApiClient(FinancialApplicationApiClient);
  const { products } = useFinancialProducts(financialApplicationApiClient);
  
  const { coreData, refreshCoreData } = useFinancialApplicationCoreData(financialApplicationId);

  useEffect(() => {
    loadBeneficiary();
  }, [personalAccountBeneficiaryId]);

  const loadBeneficiary = async () => {
    setIsLoading(true);
    try {
      const beneficiaryData = await personalAccountBeneficiaryApiClient.getPersonalAccountBeneficiary(
        financialApplicationId,
        personalAccountBeneficiaryId
      );
      setBeneficiary(beneficiaryData);
    } catch (error) {
      console.error("Failed to load beneficiary:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddBequest = async () => {
    const normalizedBequestPercentage = bequestPercentage || 0;
    if (!selectedAccountId || normalizedBequestPercentage <= 0) {
      AppToasterV2.showFailedActionToaster("Please select an account and enter a valid percentage.");
      return;
    }

    setIsAddingBequest(true);
    try {
      await personalAccountBeneficiaryApiClient.createOrUpdateBequest(
        financialApplicationId,
        personalAccountBeneficiaryId,
        selectedAccountId,
        normalizedBequestPercentage
      );
      
      AppToasterV2.showSuccessAppToaster("Bequest added successfully.");
      setSelectedAccountId("");
      setBequestPercentage(undefined);
      // Refresh both beneficiary and core data to update percentages for all beneficiaries
      await Promise.all([loadBeneficiary(), refreshCoreData()]);
    } catch (error) {
      console.error("Failed to add bequest:", error);
      AppToasterV2.showFailedActionToaster("Failed to add bequest.");
    } finally {
      setIsAddingBequest(false);
    }
  };

  const handleRemoveBequest = async (depositApplicationId: string) => {
    setRemovingBequestIds(prev => new Set(prev).add(depositApplicationId));
    try {
      await personalAccountBeneficiaryApiClient.removeBequest(
        financialApplicationId,
        personalAccountBeneficiaryId,
        depositApplicationId
      );
      
      AppToasterV2.showSuccessAppToaster("Bequest removed successfully.");
      // Refresh both beneficiary and core data to update percentages for all beneficiaries
      await Promise.all([loadBeneficiary(), refreshCoreData()]);
    } catch (error) {
      console.error("Failed to remove bequest:", error);
      AppToasterV2.showFailedActionToaster("Failed to remove bequest.");
    } finally {
      setRemovingBequestIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(depositApplicationId);
        return newSet;
      });
    }
  };

  const handleUpdateBequest = async (depositApplicationId: string, newPercentage: number) => {
    const normalizedBequestPercentage = newPercentage || 0;
    if (normalizedBequestPercentage <= 0 || normalizedBequestPercentage > 100) {
      AppToasterV2.showFailedActionToaster("Please enter a valid percentage between 1 and 100.");
      return;
    }

    setUpdatingBequestIds(prev => new Set(prev).add(depositApplicationId));
    try {
      await personalAccountBeneficiaryApiClient.createOrUpdateBequest(
        financialApplicationId,
        personalAccountBeneficiaryId,
        depositApplicationId,
        newPercentage
      );
      
      AppToasterV2.showSuccessAppToaster("Bequest updated successfully.");
      // Refresh both beneficiary and core data to update percentages for all beneficiaries
      await Promise.all([loadBeneficiary(), refreshCoreData()]);
    } catch (error) {
      console.error("Failed to update bequest:", error);
      AppToasterV2.showFailedActionToaster("Failed to update bequest.");
    } finally {
      setUpdatingBequestIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(depositApplicationId);
        return newSet;
      });
    }
  };

  // Helper functions to check if specific bequests are being modified
  const isUpdatingBequest = (depositApplicationId: string) => updatingBequestIds.has(depositApplicationId);
  const isRemovingBequest = (depositApplicationId: string) => removingBequestIds.has(depositApplicationId);

  // Get available products that haven't been bequeathed by this beneficiary yet
  const getAvailableProducts = () => {
    if (!coreData?.depositApplications || !beneficiary) {
      return [];
    }

    const personalProducts = products.filter(p => p.customerSegment === "Personal");

    const activeDepositApplications = coreData.depositApplications.filter(da => da.isActive && personalProducts.some(p => p.id === da.financialProductId));
    const thisBeneficiaryBequestedAccountIds = beneficiary.personalAccountBequests?.map(b => b.depositApplicationId) || [];

    return activeDepositApplications.filter(da => !thisBeneficiaryBequestedAccountIds.includes(da.id));
  };

  // Calculate bequest percentages for a specific deposit application
  const getBequestPercentages = (depositApplicationId: string) => {
    if (!coreData?.depositApplications || !beneficiary) {
      return { totalBequeathed: 0, thisBeneficiaryBequeathed: 0, remaining: 100 };
    }

    const depositApp = coreData.depositApplications.find(da => da.id === depositApplicationId);
    if (!depositApp) {
      return { totalBequeathed: 0, thisBeneficiaryBequeathed: 0, remaining: 100 };
    }

    // Use the deposit application's total bequeathed percentage from core data
    const totalBequeathed = depositApp.percentageBequeathed || 0;
    const thisBeneficiaryBequest = beneficiary.personalAccountBequests?.find(
      b => b.depositApplicationId === depositApplicationId
    );
    const thisBeneficiaryBequeathed = thisBeneficiaryBequest?.percentageBequeathed || 0;

    // Calculate other beneficiaries' bequeathed (total minus this beneficiary)
    const otherBeneficiariesBequeathed = Math.max(0, totalBequeathed - thisBeneficiaryBequeathed);

    // Ensure percentages don't exceed 100%
    const adjustedTotal = Math.min(totalBequeathed, 100);
    const adjustedThisBeneficiary = Math.min(thisBeneficiaryBequeathed, 100);
    const adjustedOtherBeneficiaries = Math.min(otherBeneficiariesBequeathed, 100);
    const adjustedRemaining = Math.max(0, 100 - adjustedTotal);

    return { 
      totalBequeathed: adjustedTotal, 
      thisBeneficiaryBequeathed: adjustedThisBeneficiary,
      otherBeneficiariesBequeathed: adjustedOtherBeneficiaries,
      remaining: adjustedRemaining 
    };
  };

  return {
    beneficiary,
    selectedAccountId,
    setSelectedAccountId,
    bequestPercentage,
    setBequestPercentage,
    isLoading,
    handleAddBequest,
    handleRemoveBequest,
    handleUpdateBequest,
    getAvailableProducts,
    getBequestPercentages,
    loadBeneficiary,
    products,
    isAddingBequest,
    isUpdatingBequest,
    isRemovingBequest,
  };
}; 